/**
 * 活跃用户列表组件
 *
 * 显示 48 小时窗口内的活跃用户：
 * - 搜索：按用户名/昵称/人员全称模糊搜索（防抖 300ms）
 * - 列表：头像 + 姓名 + 交互类型 + 倒计时
 * - 分页：底部页码控制
 * - 未读红点：SSE 驱动
 */

import { useEffect, useState, useCallback, useRef, type MutableRefObject } from 'react';
import { Input } from '@heroui/react';
import {
  IconSearch as Search,
  IconChevronLeft as ChevronLeft,
  IconChevronRight as ChevronRight,
  IconChevronsLeft as ChevronsLeft,
  IconChevronsRight as ChevronsRight,
} from '@tabler/icons-react';
import { fetchActiveUsers, type ActiveUser } from '../hooks/useWechatApi';
import { ActiveUserItem } from './ActiveUserItem';

interface ActiveUsersListProps {
  selectedOpenid: string | null;
  unreadMap: Record<string, boolean>;
  onSelectUser: (user: ActiveUser) => void;
  onRefreshRef: MutableRefObject<(() => void) | null>;
}

export function ActiveUsersList({
  selectedOpenid,
  unreadMap,
  onSelectUser,
  onRefreshRef,
}: ActiveUsersListProps) {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // 加载数据
  const loadUsers = useCallback(
    async (p: number = page, kw: string = keyword) => {
      setLoading(true);
      const result = await fetchActiveUsers({
        page: p,
        pageSize,
        keyword: kw || undefined,
      });
      setUsers(result.items);
      setTotal(result.total);
      setLoading(false);
    },
    [page, pageSize, keyword]
  );

  // 初始加载
  useEffect(() => {
    void loadUsers(page, keyword);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // 暴露 refresh 方法给父组件
  useEffect(() => {
    onRefreshRef.current = () => {
      void loadUsers(page, keyword);
    };
    return () => {
      onRefreshRef.current = null;
    };
  }, [onRefreshRef, loadUsers, page, keyword]);

  // 搜索防抖
  const handleSearchChange = useCallback(
    (value: string) => {
      setKeyword(value);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        setPage(1);
        void loadUsers(1, value);
      }, 300);
    },
    [loadUsers]
  );

  // 清理 debounce
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* 头部：标题 + 人数 */}
      <div className="shrink-0 border-b border-divider px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium">活跃用户</span>
          <span className="text-xs text-muted-foreground">{total} 人</span>
        </div>
        {/* 搜索框 */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="搜索用户名、昵称或姓名"
            value={keyword}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* 用户列表 */}
      <div className="flex-1 overflow-y-auto">
        {loading && users.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            加载中...
          </div>
        ) : users.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">
            暂无活跃用户
          </div>
        ) : (
          <div className="divide-y divide-divider">
            {users.map((user) => (
              <ActiveUserItem
                key={user.openid}
                user={user}
                isSelected={selectedOpenid === user.openid}
                hasUnread={!!unreadMap[user.openid]}
                onClick={() => onSelectUser(user)}
              />
            ))}
          </div>
        )}
      </div>

      {/* 分页 */}
      <div className="flex shrink-0 items-center justify-between border-t border-divider px-3 py-2">
        <span className="text-xs text-muted-foreground">
          共 {totalPages} 页，当前：{page}/{totalPages}
        </span>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => setPage(1)}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="首页"
          >
            <ChevronsLeft className="size-3.5" />
          </button>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="上一页"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="下一页"
          >
            <ChevronRight className="size-3.5" />
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
            className="p-1 rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed"
            title="末页"
          >
            <ChevronsRight className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
